import { toast } from 'sonner';
import { useState, useEffect, useCallback } from 'react';

import { useTranslate } from 'src/locales';
import { MESSENGER_APP_ID, MESSENGER_APP_SCOPES } from 'src/config';

declare global {
  interface Window {
    fbAsyncInit?: () => void;
    FB?: any;
  }
}

interface FBAuthResponse {
  authResponse?: {
    accessToken?: string;
  };
}

interface UseConnectWhatsAppProps {
  onSuccess: (data: { main_token: string }) => void;
  onClick?: (value: any) => void;
  appId?: string;
}

export const useConnectFb = ({
  onSuccess,
  onClick,
  appId = MESSENGER_APP_ID,
}: UseConnectWhatsAppProps) => {
  const [isWhatsappConnectSuccess, setIsWhatsappConnectSuccess] = useState(false);
  const [messengerToken, setMessengerToken] = useState<string | null>(null);
  const { t } = useTranslate('translation');

  const loadFbSdk = useCallback(
    () =>
      new Promise<void>((resolve) => {
        const existingScript = document.getElementById('facebook-jssdk');
        if (existingScript) {
          existingScript.remove();
          delete window.FB;
        }

        window.fbAsyncInit = () => {
          if (window.FB) {
            window.FB.init({
              appId,
              autoLogAppEvents: true,
              xfbml: true,
              version: 'v20.0',
            });
            console.log('✅ FB SDK Initialized');
            resolve();
          }
        };

        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        document.body.appendChild(script);
      }),
    [appId]
  );

  useEffect(() => {
    loadFbSdk();

    const handleMessageEvent = (event: MessageEvent) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      )
        return;

      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          // handle WA embedded signup event if needed
        }
      } catch {
        // Silently ignore invalid JSON
      }
    };

    window.addEventListener('message', handleMessageEvent);
    return () => {
      window.removeEventListener('message', handleMessageEvent);
    };
  }, [loadFbSdk]);

  const connect = useCallback(() => {
    if (onClick) onClick(true);

    if (window.FB) {
      window.FB.login(
        (response: FBAuthResponse) => {
          if (response.authResponse?.accessToken) {
            toast.success(`${t('FACEBOOK_MESSENGER')} ${t('ACCOUNT_VERIFIED_SUCCESSFULLY')}`);
            setIsWhatsappConnectSuccess(true);

            // // Debug: Check granted permissions
            // window.FB.api('/me/permissions', function (permRes: any) {
            //   console.log('🔐 Granted Permissions:', permRes);
            // });

            setMessengerToken(response?.authResponse?.accessToken || null);

            onSuccess({
              main_token: response.authResponse.accessToken,
            });
          } else {
            toast.error(`${t('FACEBOOK_MESSENGER')} ${t('ACCOUNT_VERIFICATION_FAILED')}`);
            setIsWhatsappConnectSuccess(false);
            onSuccess({
              main_token: '',
            });
          }
        },
        {
          scope: MESSENGER_APP_SCOPES?.replace(/'/g, ''),
          auth_type: 'rerequest',
        }
      );
    } else {
      toast.error(`${t('FACEBOOK_MESSENGER')} ${t('ACCOUNT_VERIFICATION_FAILED')}`);
      setIsWhatsappConnectSuccess(false);
      onSuccess({
        main_token: '',
      });
    }
  }, [onClick, onSuccess, t]);

  return { connect, isWhatsappConnectSuccess, messengerToken };
};
